import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function formatarData(valor) {
  if (!valor) return '—';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(valor));
  } catch {
    return '—';
  }
}

function labelStatus(status) {
  const labels = {
    started: 'Em andamento',
    submitted: 'Respondido',
    scored: 'Concluído'
  };

  return labels[status] || status || '—';
}

export default function AvaliacoesList() {
  const navigate = useNavigate();

  const [avaliacoes, setAvaliacoes] = useState([]);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    async function carregar() {
      try {
        setLoading(true);
        setErro('');

        const response = await fetch('/api/admin/avaliacoes', {
          method: 'GET',
          credentials: 'include'
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Não foi possível carregar as avaliações.');
        }

        setAvaliacoes(data.avaliacoes || []);
      } catch (e) {
        setErro(e.message || 'Não foi possível carregar as avaliações.');
      } finally {
        setLoading(false);
      }
    }

    carregar();
  }, []);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return avaliacoes.filter(item => {
      const bateStatus = !status || item.status === status;

      const bateBusca =
        !termo ||
        [
          item.nome,
          item.email,
          item.empresa,
          item.cargo
        ]
          .filter(Boolean)
          .some(valor =>
            String(valor).toLowerCase().includes(termo)
          );

      return bateStatus && bateBusca;
    });
  }, [avaliacoes, busca, status]);

  if (loading) {
    return (
      <div style={{ padding: '2rem 0' }}>
        Carregando avaliações...
      </div>
    );
  }

  if (erro) {
    return (
      <div
        style={{
          background: '#fff1f0',
          color: '#9f1c16',
          padding: '1rem',
          borderRadius: 8
        }}
      >
        {erro}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 20
        }}
      >
        <input
          type="search"
          placeholder="Buscar por nome, e-mail, empresa ou cargo"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{
            flex: '1 1 320px',
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid #d5d9de',
            fontSize: '1rem'
          }}
        />

        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          style={{
            minWidth: 190,
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid #d5d9de',
            fontSize: '1rem',
            background: '#fff'
          }}
        >
          <option value="">Todos os status</option>
          <option value="started">Em andamento</option>
          <option value="submitted">Respondido</option>
          <option value="scored">Concluído</option>
        </select>
      </div>

      <div
        style={{
          marginBottom: 14,
          color: '#596273',
          fontWeight: 600
        }}
      >
        {filtradas.length} avaliação(ões)
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          overflowX: 'auto',
          boxShadow: '0 2px 10px rgba(36,47,60,0.06)'
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: 1000
          }}
        >
          <thead style={{ background: '#f7f7f7' }}>
            <tr>
              <th style={th}>Participante</th>
              <th style={th}>Empresa</th>
              <th style={th}>Cargo</th>
              <th style={th}>Status</th>
              <th style={th}>Início</th>
              <th style={th}>Conclusão</th>
              <th style={{ ...th, textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>

          <tbody>
            {filtradas.map(item => (
              <tr key={item.session_id}>
                <td style={td}>
                  <div style={{ fontWeight: 700, color: '#242F3C' }}>
                    {item.nome || '—'}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: '0.88rem',
                      color: '#707987'
                    }}
                  >
                    {item.email || '—'}
                  </div>
                </td>

                <td style={td}>{item.empresa || '—'}</td>
                <td style={td}>{item.cargo || '—'}</td>

                <td style={td}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '6px 10px',
                      borderRadius: 999,
                      background:
                        item.status === 'scored'
                          ? '#e8f6ed'
                          : item.status === 'submitted'
                          ? '#fff4df'
                          : '#edf1f5',
                      color: '#242F3C',
                      fontWeight: 700,
                      fontSize: '0.86rem'
                    }}
                  >
                    {labelStatus(item.status)}
                  </span>
                </td>

                <td style={td}>
                  {formatarData(item.started_at)}
                </td>

                <td style={td}>
                  {formatarData(item.scored_at)}
                </td>

                <td style={{ ...td, textAlign: 'center' }}>
                  {item.status === 'scored' ? (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/relatorio-icv?session=${encodeURIComponent(
                            item.session_id
                          )}&acesso=admin`
                        )
                      }
                      style={{
                        background: '#F36611',
                        color: '#fff',
                        border: 'none',
                        padding: '9px 14px',
                        borderRadius: 7,
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Ver relatório
                    </button>
                  ) : (
                    <span style={{ color: '#8a929d' }}>
                      —
                    </span>
                  )}
                </td>
              </tr>
            ))}

            {filtradas.length === 0 && (
              <tr>
                <td
                  colSpan="7"
                  style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: '#707987'
                  }}
                >
                  Nenhuma avaliação encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = {
  padding: 14,
  borderBottom: '2px solid #e6e6e6',
  textAlign: 'left',
  color: '#242F3C',
  whiteSpace: 'nowrap'
};

const td = {
  padding: 14,
  borderBottom: '1px solid #eef0f2',
  verticalAlign: 'middle'
};
